import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Sparkles, Database, Shield, CheckCircle } from 'lucide-react';

const WorkflowSection = () => {
  const { t } = useTranslatedContent('workflow');
  const steps = t('steps', { returnObjects: true });

  if (!Array.isArray(steps)) return null;

  const stepIcons = [Sparkles, Database, Shield, CheckCircle];
  const gradients = [
    'from-blue-500 to-indigo-500',
    'from-indigo-500 to-violet-500',
    'from-violet-500 to-purple-500',
    'from-purple-500 to-blue-500'
  ];

  return (
    <div id="migration-process" className="py-24 relative overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">{t('subtitle')}</p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step: any, index: number) => {
            const Icon = stepIcons[index];
            return (
              <div key={index} className="relative group">
                {/* Step number */}
                <div className={`absolute -top-4 left-8 bg-gradient-to-r ${gradients[index]} w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm z-10`}>
                  {index + 1}
                </div>

                {/* Card */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 h-full hover:bg-white/20 transition-colors duration-300">
                  {/* Icon */}
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${gradients[index]} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {step.title}
                  </h3>

                  <ul className="space-y-3">
                    {step.steps.map((item: string, itemIndex: number) => (
                      <li key={itemIndex} className="flex items-start text-blue-100 group-hover:text-white transition-colors duration-300">
                        <span className="mr-2 text-blue-400 group-hover:text-blue-300">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Connecting line for desktop */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500/50 to-violet-500/50 transform -translate-y-1/2" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkflowSection;